// src/components/TopicMafia/TopicMafiaLobby.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Alert,
    CircularProgress,
    Card,
    CardActionArea,
    CardContent,
} from "@mui/material";
import api from "../../api/axios";
import { STATUS_LABEL, STATUS_COLOR } from "./topicMafiaUi";



const DEFAULT_CREATE = {
    title: "라이어 게임",
    minority_count: 1,
    turn_time_limit_sec: 25,
    vote_time_limit_sec: 30,

    // (예정) 서버 반영 전이라 disabled로만 표시
    max_players: 8,
    total_rounds: 3,
};

export default function TopicMafiaLobby() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [rooms, setRooms] = useState([]);
    const [error, setError] = useState("");

    const [openCreate, setOpenCreate] = useState(false);
    const [createForm, setCreateForm] = useState(DEFAULT_CREATE);
    const [creating, setCreating] = useState(false);

    const goRoom = useCallback(
        (roomId) => navigate(`/main/games/topic-mafia/${roomId}`),
        [navigate]
    );

    // ✅ 방 목록 불러오기 (rooms/list가 존재한다고 가정)
    const fetchRooms = useCallback(async () => {
        setError("");
        setLoading(true);
        try {
            const res = await api.get("/api/topic-mafia/rooms/");
            setRooms(res.data?.rooms ?? []);
        } catch (e) {
            setRooms([]);
            setError(e?.response?.data?.error || "방 목록을 불러오지 못했습니다.");
        } finally {
            setLoading(false);
        }
    }, []);

    const quickEnter = useCallback(async () => {
        setError("");
        try {
            const res = await api.post("/api/topic-mafia/quick-enter/", {});
            if (res.data?.ok && res.data.room_id) goRoom(res.data.room_id);
            else setError("빠른 입장 응답이 올바르지 않습니다.");
        } catch (e) {
            setError(
                e?.response?.data?.error ||
                "빠른 입장 실패: /api/topic-mafia/quick-enter/ (urls.py include 확인)"
            );
        }
    }, [goRoom]);

    const openCreateDialog = useCallback(() => {
        setCreateForm(DEFAULT_CREATE);
        setOpenCreate(true);
    }, []);

    const closeCreateDialog = useCallback(() => {
        if (!creating) setOpenCreate(false);
    }, [creating]);

    const createRoom = useCallback(async () => {
        setError("");
        setCreating(true);
        try {
            const payload = {
                title: createForm.title,
                minority_count: createForm.minority_count,
                turn_time_limit_sec: createForm.turn_time_limit_sec,
                vote_time_limit_sec: createForm.vote_time_limit_sec,

                // ✅ 모델 필드 추가 후 활성화할 값들
                // max_players: createForm.max_players,
                // total_rounds: createForm.total_rounds,
            };

            const res = await api.post("/api/topic-mafia/rooms/create/", payload);

            if (res.data?.ok && res.data.room_id) {
                setOpenCreate(false);
                goRoom(res.data.room_id);
            } else {
                setError("방 만들기 응답이 올바르지 않습니다.");
            }
        } catch (e) {
            setError(e?.response?.data?.error || "방 만들기 실패");
        } finally {
            setCreating(false);
        }
    }, [createForm, goRoom]);

    useEffect(() => {
        fetchRooms();
    }, [fetchRooms]);

    const sortedRooms = useMemo(() => {
        return [...rooms].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    }, [rooms]);

    return (
        <Box sx={{ maxWidth: 980, mx: "auto", p: 2 }}>
            <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        justifyContent="space-between"
                        spacing={1.25}
                    >
                        {/* 좌측: 제목/설명 */}
                        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 900,
                                    lineHeight: 1.15,
                                    // ✅ 모바일에서 글자 쪼개짐 방지
                                    wordBreak: "keep-all",
                                    overflowWrap: "normal",
                                    whiteSpace: "nowrap",     // 한 줄 유지
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                }}
                            >
                                🎭 라이어 게임
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    // ✅ 설명은 2줄까지 허용 (너무 길면 줄바꿈)
                                    wordBreak: "keep-all",
                                    overflowWrap: "anywhere",
                                    lineHeight: 1.3,
                                }}
                            >
                                방 선택 또는 방을 만들어 주세요.
                            </Typography>
                        </Stack>

                        {/* 우측: 버튼들 (모바일에서는 2줄 배치) */}
                        <Stack spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    onClick={fetchRooms}
                                    disabled={loading}
                                    fullWidth
                                    sx={{ height: 44, fontWeight: 900, whiteSpace: "nowrap" }}
                                >
                                    목록 새로고침
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={quickEnter}
                                    fullWidth
                                    sx={{ height: 44, fontWeight: 900, whiteSpace: "nowrap" }}
                                >
                                    ⚡ 빠른 입장
                                </Button>
                            </Stack>

                            <Button
                                variant="contained"
                                onClick={openCreateDialog}
                                fullWidth
                                sx={{ height: 44, fontWeight: 900, whiteSpace: "nowrap" }}
                            >
                                ➕ 방 만들기
                            </Button>
                        </Stack>
                    </Stack>

                    {error && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Paper>

                <Paper sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            방 목록
                        </Typography>
                        <Chip size="small" label={`총 ${sortedRooms.length}개`} />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 5 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 1, color: "text.secondary" }}>
                                불러오는 중...
                            </Typography>
                        </Stack>
                    ) : sortedRooms.length === 0 ? (
                        <Alert severity="info">
                            현재 표시할 방이 없어. <b>방 만들기</b> 또는 <b>빠른 입장</b>으로 시작해보자.
                        </Alert>
                    ) : (
                        <Stack spacing={1.25}>
                            {sortedRooms.map((r) => {
                                return (
                                    <Card key={r.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardActionArea onClick={() => goRoom(r.id)}>
                                            <CardContent sx={{ p: 1.75 }}>
                                                {(() => {
                                                    const status = r.status || "WAITING";
                                                    const playerCount =
                                                        r.player_count ?? r.players_count ?? r.playerCount ?? null;
                                                    const maxPlayers = r.max_players ?? r.maxPlayers ?? null;

                                                    // ✅ 공통 chip 스타일 (볼드 제거)
                                                    const chipSx = {
                                                        height: 28,
                                                        borderRadius: 999,
                                                        "& .MuiChip-label": {
                                                            px: 1.1,
                                                            lineHeight: 1,
                                                        },
                                                    };

                                                    return (
                                                        <Stack spacing={1}>
                                                            {/* 제목 + 상태 */}
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                spacing={1}
                                                                sx={{ minWidth: 0 }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        minWidth: 0,
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {r.title || `라이어 게임 #${r.id}`}
                                                                </Typography>

                                                                <Chip
                                                                    size="small"
                                                                    label={STATUS_LABEL[status] || status}
                                                                    color={STATUS_COLOR[status] || "default"}
                                                                    sx={{ ...chipSx, height: 30 }}
                                                                />
                                                            </Stack>

                                                            {/* 정보 칩들 */}
                                                            <Stack
                                                                direction="row"
                                                                spacing={0.8}
                                                                flexWrap="wrap"
                                                                alignItems="center"
                                                                useFlexGap
                                                                sx={{ rowGap: 0.8 }}
                                                            >
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label={`방 ID: ${r.id}`}
                                                                    sx={chipSx}
                                                                />

                                                                {playerCount != null && (
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={
                                                                            maxPlayers != null
                                                                                ? `인원: ${playerCount}/${maxPlayers}`
                                                                                : `인원: ${playerCount}`
                                                                        }
                                                                        sx={chipSx}
                                                                    />
                                                                )}

                                                                {r.host_id != null && (
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label={`호스트: ${r.host_nickname ?? r.host_id}`}
                                                                        sx={chipSx}
                                                                    />
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                    );
                                                })()}
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>
            </Stack>

            {/* Create Room Dialog */}
            <Dialog open={openCreate} onClose={closeCreateDialog} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 900 }}>➕ 방 만들기</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="방 제목"
                            value={createForm.title}
                            onChange={(e) =>
                                setCreateForm((p) => ({ ...p, title: e.target.value }))
                            }
                            fullWidth
                            inputProps={{ maxLength: 80 }}
                        />

                        <TextField
                            select
                            label="소수(마피아) 인원"
                            value={createForm.minority_count}
                            onChange={(e) =>
                                setCreateForm((p) => ({ ...p, minority_count: Number(e.target.value) }))
                            }
                            fullWidth
                        >
                            {[1, 2, 3].map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}명
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="턴 제한 시간"
                            value={createForm.turn_time_limit_sec}
                            onChange={(e) =>
                                setCreateForm((p) => ({
                                    ...p,
                                    turn_time_limit_sec: Number(e.target.value),
                                }))
                            }
                            fullWidth
                            helperText="각 플레이어 발언 제한 시간(초)"
                        >
                            {[15, 20, 25, 30, 40, 60].map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}초
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="투표 제한 시간"
                            value={createForm.vote_time_limit_sec}
                            onChange={(e) =>
                                setCreateForm((p) => ({
                                    ...p,
                                    vote_time_limit_sec: Number(e.target.value),
                                }))
                            }
                            fullWidth
                            helperText="투표 단계 제한 시간(초)"
                        >
                            {[15, 20, 25, 30, 40, 60].map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}초
                                </MenuItem>
                            ))}
                        </TextField>

                        <Alert severity="info">
                            <b>최대 인원 / 라운드 수</b>는 “호스트 설정”으로 가는 게 맞아.
                            모델/서비스에 필드 반영되면 입력 활성화하고 create payload에 같이 보내면 돼.
                        </Alert>

                        <Stack direction="row" spacing={1}>
                            <TextField
                                label="(예정) 최대 인원"
                                type="number"
                                value={createForm.max_players}
                                fullWidth
                                disabled
                            />
                            <TextField
                                label="(예정) 라운드 수"
                                type="number"
                                value={createForm.total_rounds}
                                fullWidth
                                disabled
                            />
                        </Stack>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={closeCreateDialog} color="inherit" disabled={creating}>
                        취소
                    </Button>
                    <Button variant="contained" onClick={createRoom} disabled={creating}>
                        {creating ? "생성 중..." : "생성"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
